import React, {useEffect} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Box, Stack, styled} from '@mui/material';
import {
  Body,
  designSystemToken,
  Icon,
  IconName,
  Label,
  SwitchButton,
  Tooltip,
} from '@lightricks/react-design-system';
import StyledColorPicker from '@contentlab/components/styled-color-picker';
import contentLabStore, {
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {hexToRgb, rgbToHex} from '@contentlab/utils/colorConverter';
import translate from '@/utils/translate';
import {RgbaColor} from '@/types/rgbaColor';
import Divider from '@/components/divider';

const DEFAULT_BACKGROUND_COLOR: RgbaColor = {r: 135, g: 70, b: 184, a: 255};

const BackgroundOptionsItemContainer = styled(Box, {
  shouldForwardProp: (prop: string) => prop !== 'isSelected',
})<{isSelected: boolean}>(({isSelected}) => ({
  'height': '124px',
  'width': '100%',
  'padding': '16px',
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'flexDirection': 'column',
  'boxSizing': 'border-box',
  'gap': '8px',
  'borderRadius': '16px',
  'flex': '1',
  'cursor': 'pointer',
  'border': `1.5px solid ${
    isSelected
      ? designSystemToken('semantic.bg.neutral')
      : designSystemToken('semantic.bg.neutral-inverse')
  }`,
  'backgroundColor': isSelected
    ? 'transparent'
    : designSystemToken('semantic.bg.primary'),
  'color': designSystemToken('semantic.fg.primary'),
  '&:hover': {
    borderColor: designSystemToken('semantic.bg.neutral'),
    backgroundColor: designSystemToken('semantic.bg.tertiary'),
  },
}));

enum BackgroundOption {
  TRANSPARENT = 'transparent',
  ORIGINAL = 'original',
  SOLID_COLOR = 'solid_color',
}

const BackgroundOptions: Record<
  BackgroundOption,
  {label: string; icon: IconName}
> = {
  [BackgroundOption.TRANSPARENT]: {
    label: 'Transparent',
    icon: 'Features-Chroma',
  },
  [BackgroundOption.ORIGINAL]: {
    label: 'Original',
    icon: 'Navigation-Photos-Light',
  },
  [BackgroundOption.SOLID_COLOR]: {
    label: 'Solid Color',
    icon: 'Features-Smile-Color',
  },
};

function getSelectedOption(
  removeBackground: boolean,
  backgroundColor?: RgbaColor
) {
  if (backgroundColor) return BackgroundOption.SOLID_COLOR;
  if (removeBackground) return BackgroundOption.TRANSPARENT;
  return BackgroundOption.ORIGINAL;
}

const ColorPicker = React.forwardRef<
  HTMLInputElement,
  {
    backgroundColor: RgbaColor;
    handleColorChange: (color: RgbaColor) => void;
  }
>(({backgroundColor, handleColorChange}, ref) => {
  return (
    <StyledColorPicker
      inputRef={ref}
      value={rgbToHex(backgroundColor.r, backgroundColor.g, backgroundColor.b)}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const color = e.target.value;
        const rgbColor = hexToRgb(color);
        handleColorChange({
          r: rgbColor.r,
          g: rgbColor.g,
          b: rgbColor.b,
          a: 255,
        });
      }}
      sx={{
        'position': 'absolute',
        'width': '14px',
        'height': '14px',
        '& .MuiInputBase-root': {
          width: '14px',
          height: '14px',
          borderRadius: '1px',
          border: 'none',
        },
        '& fieldset': {
          border: 'none',
        },
      }}
    />
  );
});
ColorPicker.displayName = 'ColorPicker';

interface BackgroundOptionItemProps {
  option: BackgroundOption;
  isSelected: boolean;
  onClick: () => void;
  backgroundColor?: RgbaColor;
  handleColorChange: (color: RgbaColor) => void;
}

function BackgroundOptionItem({
  option,
  isSelected,
  onClick,
  backgroundColor,
  handleColorChange,
}: BackgroundOptionItemProps) {
  const data = BackgroundOptions[option];
  const colorPickerRef = React.useRef<HTMLInputElement>(null);

  const handleContainerClick = () => {
    // If it's the solid color option and already selected, trigger the color picker immediately
    if (
      option === BackgroundOption.SOLID_COLOR &&
      isSelected &&
      colorPickerRef.current
    ) {
      colorPickerRef.current.click();
    } else {
      // Otherwise, handle the normal selection logic
      onClick();
    }
  };

  return (
    <BackgroundOptionsItemContainer
      isSelected={isSelected}
      onClick={handleContainerClick}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          alignSelf: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
        }}
      >
        <Icon
          name={data.icon}
          size="medium"
          appearance="neutral"
          style={{width: '28px', height: '28px'}}
        />
        {option === BackgroundOption.SOLID_COLOR && backgroundColor && (
          <ColorPicker
            ref={colorPickerRef}
            backgroundColor={backgroundColor}
            handleColorChange={handleColorChange}
          />
        )}
      </Box>

      <Label size="md" color={designSystemToken('semantic.fg.neutral')}>
        {option === BackgroundOption.SOLID_COLOR && backgroundColor
          ? rgbToHex(
              backgroundColor.r,
              backgroundColor.g,
              backgroundColor.b
            ).toUpperCase()
          : translate(data.label)}
      </Label>
    </BackgroundOptionsItemContainer>
  );
}

interface AvatarBackgroundSectionProps {
  testID?: string;
  removeBackground: boolean;
  backgroundColor?: RgbaColor;
  circleMask: boolean;
  handleBackgroundChange: (
    removeBackground: boolean,
    backgroundColor?: RgbaColor
  ) => void;
  toggleCircleMask: (circleMask?: boolean) => void;
}

function AvatarBackgroundSection({
  testID,
  removeBackground,
  backgroundColor,
  circleMask,
  handleBackgroundChange,
  toggleCircleMask,
}: AvatarBackgroundSectionProps) {
  const selectedOption = getSelectedOption(removeBackground, backgroundColor);
  const lastSolidColor = contentLabStore(
    useShallow((state) => state.draftAiAvatarProject.lastSolidColor)
  );

  // Save the current color when it's a solid color
  useEffect(() => {
    if (backgroundColor) {
      contentLabActions.updateDraftAiAvatarProject({
        lastSolidColor: backgroundColor,
      });
    }
  }, [backgroundColor]);

  const handleOptionClick = (option: BackgroundOption) => {
    switch (option) {
      case BackgroundOption.TRANSPARENT:
        handleBackgroundChange(true, undefined);
        toggleCircleMask(false);
        break;
      case BackgroundOption.ORIGINAL:
        handleBackgroundChange(false, undefined);
        break;
      case BackgroundOption.SOLID_COLOR:
        // If not currently selected, restore last color or use default
        if (selectedOption !== BackgroundOption.SOLID_COLOR) {
          handleBackgroundChange(
            true,
            lastSolidColor || DEFAULT_BACKGROUND_COLOR
          );
        }
        // Color picker will be handled by the ColorPicker component in the UI
        break;
      default:
        break;
    }
  };

  return (
    <Stack direction="column" padding="0px 0px 16px 0px">
      <Stack
        data-testid={testID}
        direction="row"
        gap="16px"
        width="100%"
        justifyContent="space-around"
        boxSizing="border-box"
      >
        {Object.values(BackgroundOption).map((option) => (
          <BackgroundOptionItem
            key={option}
            option={option}
            isSelected={selectedOption === option}
            onClick={() => handleOptionClick(option)}
            backgroundColor={backgroundColor}
            handleColorChange={(color: RgbaColor) =>
              handleBackgroundChange(true, color)
            }
          />
        ))}
      </Stack>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Body size="md" color={designSystemToken('semantic.fg.primary')}>
          {translate('Circular frame')}
        </Body>
        <Tooltip
          title={translate(
            'Circular frame isn’t available with a transparent background.'
          )}
          arrow
          type="light"
          placement="top"
          sx={{
            width: '226px',
          }}
          disableHoverListener={selectedOption !== BackgroundOption.TRANSPARENT}
        >
          <span>
            <SwitchButton
              checked={
                circleMask && selectedOption !== BackgroundOption.TRANSPARENT
              }
              onChange={() => toggleCircleMask()}
              disabled={selectedOption === BackgroundOption.TRANSPARENT}
              checkedTrackColor={designSystemToken('semantic.bg.neutral')}
            />
          </span>
        </Tooltip>
      </Box>
    </Stack>
  );
}

export default AvatarBackgroundSection;
